import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Heading,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Text,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

const AnimalEpidemicChart = ({ data }) => {
  const [selectedAnimal, setSelectedAnimal] = useState("");

  // จัดการการเปลี่ยนแปลงใน dropdown
  const handleAnimalChange = (e) => {
    setSelectedAnimal(e.target.value);
  };

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    // กรองข้อมูลตามชนิดสัตว์ที่เลือก
    const filteredData = selectedAnimal
      ? data.filter((item) => item.AnimalName === selectedAnimal)
      : data;

    // กรองข้อมูลตามชนิดสัตว์
    const animalTypes = [...new Set(filteredData.map((item) => item.AnimalName))].filter(Boolean);
    const diseases = [...new Set(filteredData.map((item) => item.DiseaseName))].filter(Boolean);

    // สร้างข้อมูลสำหรับแต่ละชนิดสัตว์ โดยแยกตามชื่อโรค
    return animalTypes.map((animal) => {
      const dataPoint = { name: animal };
      diseases.forEach((disease) => {
        const relevantData = filteredData.filter(
          (item) => item.AnimalName === animal && item.DiseaseName === disease
        );
        const total = relevantData.reduce((sum, item) => sum + item.Sick, 0);
        dataPoint[disease] = total;
      });
      return dataPoint;
    });
  };

  // สร้างสีสำหรับแต่ละชื่อโรค
  const diseases = [...new Set(data.map((item) => item.DiseaseName))].filter(Boolean);
  const colors = [
    "#16A085", // Green
    "#3498DB", // Blue
    "#E74C3C", // Red
    "#F1C40F", // Yellow
    "#9B59B6", // Purple
  ];

  // รายการชนิดสัตว์สำหรับ dropdown
  const animalTypes = [...new Set(data.map((item) => item.AnimalName))].filter(Boolean);

  // Tooltip ที่กำหนดเอง
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <p>
            <strong>ชนิดสัตว์:</strong> {label}
          </p>
          {payload.map((entry) => (
            <p key={entry.name}>
              <strong>{entry.name}:</strong> {entry.value}
            </p>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mt={6}
      bg="white"
    >
        <StandardHeader
          title="กราฟการเกิดโรคระบาดในสัตว์"
          bgColor="iGreen.600"
          icon={faChartBar}
        />
      <FormControl m={5} maxW="300px">
        <FormLabel fontSize="xl" color="gray">
          เลือกชนิดสัตว์
        </FormLabel>
        <Select
          value={selectedAnimal}
          onChange={handleAnimalChange}
          placeholder="ทุกชนิดสัตว์"
          colorScheme="iBlue"
        >
          {animalTypes.map((animal) => (
            <option key={animal} value={animal}>
              {animal}
            </option>
          ))}
        </Select>
      </FormControl>
      {chartData().length > 0 ? (
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={chartData()}
            margin={{ top: 40, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              interval={0}
              height={150}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              label={{
                value: "จำนวนสัตว์ป่วย (ตัว)",
                angle: -90,
                position: "insideLeft",
                offset: -5,
              }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="horizontal"
              verticalAlign="top"
              align="right"
              wrapperStyle={{ paddingBottom: 10 }}
            />
            {diseases.map((disease, index) => (
              <Bar
                key={disease}
                dataKey={disease}
                name={disease}
                fill={colors[index % colors.length]}
                barSize={50 / diseases.length}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Text textAlign="center" color="gray.500" p={5}>
          ไม่มีข้อมูลสำหรับแสดงกราฟ
        </Text>
      )}
    </Box>
  );
};

export default AnimalEpidemicChart;